import { logError } from '@/utils/logger';
import SearchableSelect from '@/components/SearchableSelect';
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Package as PackageIcon, TShirt, FirstAid, Funnel, FilePdf, MagnifyingGlass, ArrowDown, ArrowUp, UserCircleGear, Pencil, Trash } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import ExportButton from '@/components/ExportButton';
import { SortableHeader, useSort } from '@/components/SortableHeader';

const STOCK_TYPES = [
  { value: 'bottine', label: 'Bottine' }, { value: 'kepi', label: 'Képi' },
  { value: 'matraque', label: 'Matraque' }, { value: 'gondo', label: 'Gondo' },
  { value: 'ceinture', label: 'Ceinture' }, { value: 'tenue', label: 'Tenue' },
  { value: 'radio', label: 'Radio' }, { value: 'lampe', label: 'Lampe' },
  { value: 'gilet', label: 'Gilet' },
];

const TAB_STOCK = 'stock';
const TAB_ENTRIES = 'entries';
const TAB_DISTRIBUTIONS = 'distributions';
const TAB_NEEDS = 'needs';

const Logistics = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(TAB_STOCK);
  const [items, setItems] = useState([]);
  const [entries, setEntries] = useState([]);
  const [distributions, setDistributions] = useState([]);
  const [agents, setAgents] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedZone, setSelectedZone] = useState('all');
  const [userPermissions, setUserPermissions] = useState(null);
  const [userRole, setUserRole] = useState('');

  // Dialogs
  const [showEntryDialog, setShowEntryDialog] = useState(false);
  const [showDistDialog, setShowDistDialog] = useState(false);
  const [showEditStockDialog, setShowEditStockDialog] = useState(false);
  const [editingStock, setEditingStock] = useState(null);

  // Entry form
  const [entryForm, setEntryForm] = useState({
    name: '', type: 'bottine', category: 'uniform', quantity: 1, condition: 'new', source: '', date: new Date().toISOString().split('T')[0], zone_id: '',
  });

  // Distribution form
  const [distForm, setDistForm] = useState({ agent_id: '', date: new Date().toISOString().split('T')[0], zone_id: '' });
  const [distSearch, setDistSearch] = useState('');
  const [distAgentFilter, setDistAgentFilter] = useState('all');
  const [ficheSearch, setFicheSearch] = useState('');
  const [distItems, setDistItems] = useState([]);
  const [customType, setCustomType] = useState('');
  const [needs, setNeeds] = useState([]);
  const [needSearch, setNeedSearch] = useState('');
  const [showNeedDialog, setShowNeedDialog] = useState(false);
  const [needForm, setNeedForm] = useState({ description: '', item_name: '', item_type: '', quantity: 1, unit_price: '', urgent: false, due_date: '' });

  const fetchNeeds = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/purchase-needs`, { credentials: 'include' });
      if (res.ok) setNeeds(await res.json());
    } catch (error) { logError('Fetch needs:', error); }
  };

  const handleNeedSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/purchase-needs`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...needForm, unit_price: parseFloat(needForm.unit_price) || 0 }),
      });
      if (res.ok) {
        toast.success("Besoin d'achat enregistré");
        setShowNeedDialog(false);
        setNeedForm({ description: '', item_name: '', item_type: '', quantity: 1, unit_price: '', urgent: false, due_date: '' });
        fetchNeeds();
      } else { const err = await res.json(); toast.error(err.detail || 'Erreur'); }
    } catch { toast.error('Erreur'); }
  };

  const handleNeedStatus = async (needId, status) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/purchase-needs/${needId}`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.stock_updated) {
          toast.success('Achat confirmé — article ajouté automatiquement au stock ✓');
          fetchInitialData();
        } else {
          toast.success('Mis à jour');
        }
        fetchNeeds();
      }
    } catch { toast.error('Erreur'); }
  };

  const handleNeedDelete = async (needId) => {
    if (!window.confirm('Supprimer ce besoin ?')) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/purchase-needs/${needId}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { toast.success('Supprimé'); fetchNeeds(); }
      else { const err = await res.json(); toast.error(err.detail || 'Erreur'); }
    } catch { toast.error('Erreur'); }
  };

  const { sortConfig, onSort, sortData } = useSort('name', 'asc');

  const zoneParam = selectedZone && selectedZone !== 'all' ? `?zone_id=${selectedZone}` : '';

  const fetchAll = useCallback(async () => {
    try {
      const [itemsRes, entriesRes, distRes, agentsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/logistics${zoneParam}`, { credentials: 'include' }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/logistics/entries${zoneParam}`, { credentials: 'include' }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/logistics/distributions${zoneParam}`, { credentials: 'include' }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/agents${zoneParam}`, { credentials: 'include' }),
      ]);
      if (itemsRes.ok) setItems(await itemsRes.json());
      if (entriesRes.ok) setEntries(await entriesRes.json());
      if (distRes.ok) setDistributions(await distRes.json());
      if (agentsRes.ok) setAgents(await agentsRes.json());
    } catch (error) { logError('Fetch logistics:', error); }
  }, [zoneParam]);

  const fetchInitialData = useCallback(async () => {
    try {
      const [userRes, zonesRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, { credentials: 'include' }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/zones`, { credentials: 'include' }),
      ]);
      if (userRes.ok) {
        const userData = await userRes.json();
        setUserPermissions({...userData.permissions, effective: userData.effective_permissions || {}});
        setUserRole(userData.role);
        if (!userData.permissions.can_view_all_zones && userData.zone_id) setSelectedZone(userData.zone_id);
      }
      if (zonesRes.ok) {
        const z = await zonesRes.json();
        setZones(z);
        if (z.length > 0) {
          setEntryForm(prev => ({ ...prev, zone_id: prev.zone_id || z[0].zone_id }));
          setDistForm(prev => ({ ...prev, zone_id: prev.zone_id || z[0].zone_id }));
        }
      }
    } catch (error) { logError('Init:', error); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchInitialData(); fetchNeeds(); }, [fetchInitialData]);
  useEffect(() => { if (zones.length > 0) fetchAll(); }, [zones.length, selectedZone, fetchAll]);

  const canEdit = userPermissions?.effective?.logistics?.write;
  const getZoneName = (zid) => zones.find(z => z.zone_id === zid)?.name || zid;
  const getAgentName = (aid) => agents.find(a => a.agent_id === aid)?.name || aid;
  const getTypeLabel = (t) => STOCK_TYPES.find(s => s.value === t)?.label || t;
  const fmtDate = (d) => { try { return new Date(d).toLocaleDateString('fr-FR'); } catch { return d; } };

  // ======== ENTRY SUBMIT ========
  const handleEntrySubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/logistics/entries`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(entryForm),
      });
      if (res.ok) {
        toast.success('Entrée stock enregistrée');
        setShowEntryDialog(false);
        setEntryForm({ name: '', type: 'bottine', category: 'uniform', quantity: 1, condition: 'new', source: '', date: new Date().toISOString().split('T')[0], zone_id: zones[0]?.zone_id || '' });
        fetchAll();
      } else { const err = await res.json(); toast.error(err.detail || 'Erreur'); }
    } catch { toast.error('Erreur'); }
  };

  // ======== DISTRIBUTION SUBMIT ========
  const handleDistSubmit = async (e) => {
    e.preventDefault();
    if (!distForm.agent_id || distItems.length === 0) {
      toast.error('Sélectionnez un agent et au moins un article');
      return;
    }
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/logistics/distributions`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...distForm, items: distItems }),
      });
      if (res.ok) {
        toast.success('Distribution enregistrée');
        setShowDistDialog(false);
        setDistForm({ agent_id: '', date: new Date().toISOString().split('T')[0], zone_id: zones[0]?.zone_id || '' });
        setDistItems([]);
        fetchAll();
      } else { const err = await res.json(); toast.error(err.detail || 'Erreur'); }
    } catch { toast.error('Erreur'); }
  };

  const addDistItem = (item_id) => {
    if (distItems.find(d => d.item_id === item_id)) return;
    const stock = items.find(i => i.item_id === item_id);
    if (stock) setDistItems([...distItems, { item_id, name: stock.name, type: stock.type, quantity: 1, max: stock.quantity_available }]);
  };

  const updateDistItemQty = (item_id, qty) => {
    setDistItems(distItems.map(d => d.item_id === item_id ? { ...d, quantity: Math.min(qty, d.max) } : d));
  };

  const removeDistItem = (item_id) => {
    setDistItems(distItems.filter(d => d.item_id !== item_id));
  };

  // Summary stats
  const totalStock = items.reduce((s, i) => s + (i.quantity || 0), 0);
  const totalAvailable = items.reduce((s, i) => s + (i.quantity_available || 0), 0);
  const totalAssigned = items.reduce((s, i) => s + (i.quantity_assigned || 0), 0);

  // Admin: Delete & Edit
  const handleDeleteStock = async (itemId) => {
    if (!window.confirm('Supprimer cet article du stock ?')) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/logistics/${itemId}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { toast.success('Article supprimé'); fetchAll(); }
      else { const err = await res.json(); toast.error(err.detail || 'Erreur'); }
    } catch { toast.error('Erreur'); }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Supprimer cette entrée ?')) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/logistics/entries/${entryId}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { toast.success('Entrée supprimée'); fetchAll(); }
      else { const err = await res.json(); toast.error(err.detail || 'Erreur'); }
    } catch { toast.error('Erreur'); }
  };

  const handleDeleteDist = async (distId) => {
    if (!window.confirm('Supprimer cette distribution ?')) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/logistics/distributions/${distId}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { toast.success('Distribution supprimée'); fetchAll(); }
      else { const err = await res.json(); toast.error(err.detail || 'Erreur'); }
    } catch { toast.error('Erreur'); }
  };

  const openEditStock = (item) => {
    setEditingStock({ ...item });
    setShowEditStockDialog(true);
  };

  const handleEditStockSubmit = async (e) => {
    e.preventDefault();
    try {
      const { item_id, ...data } = editingStock;
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/logistics/${item_id}`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (res.ok) { toast.success('Article modifié'); setShowEditStockDialog(false); setEditingStock(null); fetchAll(); }
      else { const err = await res.json(); toast.error(err.detail || 'Erreur'); }
    } catch { toast.error('Erreur'); }
  };

  if (loading) return <div className="text-[#94A3B8]">{t('common.loading')}</div>;

  return (
    <div className="space-y-6" data-testid="logistics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold text-[#F8FAFC] tracking-tight">{t('logistics.title')}</h1>
          <p className="text-[#94A3B8] mt-1 text-sm">Gestion du stock, entrées et distributions</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {userPermissions?.can_view_all_zones && (
            <div className="w-[180px]">
              <SearchableSelect value={selectedZone} onValueChange={setSelectedZone} options={[{ value: 'all', label: 'Toutes les zones' }, ...zones.map(z => ({ value: z.zone_id, label: z.name }))]} placeholder="Toutes les zones" testId="zone-filter" />
            </div>
          )}
          <ExportButton endpoint="logistics" filename="logistique" label="Export Excel" />
          {canEdit && (
            <>
              <Button onClick={() => setShowEntryDialog(true)} className="bg-[#10B981] hover:bg-[#059669] text-white" data-testid="add-entry-button">
                <ArrowDown size={18} className="mr-2" weight="bold" /> Entrée Stock
              </Button>
              <Button onClick={() => setShowDistDialog(true)} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="add-distribution-button">
                <UserCircleGear size={18} className="mr-2" weight="bold" /> Distribution
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-[#0F172A] border border-[#1E293B] rounded-md p-5">
          <div className="flex items-center gap-2 mb-1"><PackageIcon size={20} className="text-[#2563EB]" /><span className="text-xs text-[#94A3B8] uppercase">Stock Total</span></div>
          <p className="text-2xl font-bold text-[#F8FAFC]">{totalStock}</p>
        </div>
        <div className="bg-[#0F172A] border border-[#10B981]/30 rounded-md p-5">
          <div className="flex items-center gap-2 mb-1"><ArrowDown size={20} className="text-[#10B981]" /><span className="text-xs text-[#94A3B8] uppercase">Disponible</span></div>
          <p className="text-2xl font-bold text-[#10B981]">{totalAvailable}</p>
        </div>
        <div className="bg-[#0F172A] border border-[#F59E0B]/30 rounded-md p-5">
          <div className="flex items-center gap-2 mb-1"><ArrowUp size={20} className="text-[#F59E0B]" /><span className="text-xs text-[#94A3B8] uppercase">Distribué</span></div>
          <p className="text-2xl font-bold text-[#F59E0B]">{totalAssigned}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-[#0F172A] border border-[#1E293B] rounded-md p-1">
        {[
          { key: TAB_STOCK, label: 'Stock', count: items.length },
          { key: TAB_ENTRIES, label: 'Entrées', count: entries.length },
          { key: TAB_DISTRIBUTIONS, label: 'Distributions', count: distributions.length },
          { key: TAB_NEEDS, label: "Besoins d'achat", count: needs.filter(n => n.status === 'pending').length },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            data-testid={`tab-${tab.key}`}
            className={`flex-1 px-4 py-2.5 rounded text-sm font-medium transition-colors ${
              activeTab === tab.key
                ? 'bg-[#2563EB] text-white'
                : 'text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[#1E293B]'
            }`}
          >
            {tab.label} <span className="ml-1 opacity-70">({tab.count})</span>
          </button>
        ))}
      </div>

      {/* ======== TAB: STOCK ======== */}
      {activeTab === TAB_STOCK && (
        <div className="bg-[#0F172A] border border-[#1E293B] rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#1E293B]/50">
                <tr>
                  <SortableHeader label="Article" sortKey="name" sortConfig={sortConfig} onSort={onSort} />
                  <SortableHeader label="Type" sortKey="type" sortConfig={sortConfig} onSort={onSort} />
                  <SortableHeader label="Zone" sortKey="zone_id" sortConfig={sortConfig} onSort={onSort} />
                  <SortableHeader label="Stock Total" sortKey="quantity" sortConfig={sortConfig} onSort={onSort} />
                  <SortableHeader label="Disponible" sortKey="quantity_available" sortConfig={sortConfig} onSort={onSort} />
                  <SortableHeader label="Distribué" sortKey="quantity_assigned" sortConfig={sortConfig} onSort={onSort} />
                  <SortableHeader label="État" sortKey="condition" sortConfig={sortConfig} onSort={onSort} />
                  {userRole === 'admin' && <th className="px-6 py-4 text-right text-sm font-medium text-[#F8FAFC] uppercase tracking-wider">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#1E293B]">
                {items.length === 0 ? (
                  <tr><td colSpan={userRole === 'admin' ? 8 : 7} className="px-6 py-8 text-center text-[#94A3B8]">Aucun article en stock</td></tr>
                ) : sortData(items).map(item => (
                  <tr key={item.item_id} className="hover:bg-[#1E293B]/30 transition-colors" data-testid={`stock-row-${item.item_id}`}>
                    <td className="px-6 py-4 text-[#F8FAFC] font-medium">{item.name}</td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {(item.type || '').split(',').filter(Boolean).map(t => (
                          <Badge key={t} className="bg-[#2563EB]/20 text-[#2563EB] border border-[#2563EB]/30 text-xs capitalize">{getTypeLabel(t)}</Badge>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4"><Badge className="bg-[#2563EB]/20 text-[#2563EB] border border-[#2563EB]/30">{getZoneName(item.zone_id)}</Badge></td>
                    <td className="px-6 py-4 text-[#F8FAFC] font-medium">{item.quantity}</td>
                    <td className="px-6 py-4"><span className={`font-medium ${item.quantity_available > 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>{item.quantity_available || 0}</span></td>
                    <td className="px-6 py-4 text-[#F59E0B] font-medium">{item.quantity_assigned || 0}</td>
                    <td className="px-6 py-4">
                      <Badge className={`border ${
                        item.condition === 'new' ? 'bg-[#10B981]/20 text-[#10B981] border-[#10B981]/30' :
                        item.condition === 'good' ? 'bg-[#2563EB]/20 text-[#2563EB] border-[#2563EB]/30' :
                        item.condition === 'fair' ? 'bg-[#F59E0B]/20 text-[#F59E0B] border-[#F59E0B]/30' :
                        'bg-[#EF4444]/20 text-[#EF4444] border-[#EF4444]/30'
                      }`}>{item.condition === 'new' ? 'Neuf' : item.condition === 'good' ? 'Bon' : item.condition === 'fair' ? 'Moyen' : 'Mauvais'}</Badge>
                    </td>
                    {userRole === 'admin' && (
                      <td className="px-6 py-4 text-right">
                        <Button size="sm" variant="ghost" onClick={() => openEditStock(item)} className="text-[#2563EB] hover:text-white hover:bg-[#2563EB]"><Pencil size={16} /></Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDeleteStock(item.item_id)} className="text-[#EF4444] hover:text-white hover:bg-[#EF4444]"><Trash size={16} /></Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ======== TAB: ENTRÉES ======== */}
      {activeTab === TAB_ENTRIES && (
        <div className="bg-[#0F172A] border border-[#1E293B] rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#1E293B]/50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#F8FAFC] uppercase">Date</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#F8FAFC] uppercase">Article</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#F8FAFC] uppercase">Type</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#F8FAFC] uppercase">Quantité</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#F8FAFC] uppercase">État</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#F8FAFC] uppercase">Source</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#F8FAFC] uppercase">Zone</th>
                  {userRole === 'admin' && <th className="px-6 py-4 text-right text-sm font-medium text-[#F8FAFC] uppercase">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#1E293B]">
                {entries.length === 0 ? (
                  <tr><td colSpan={userRole === 'admin' ? 8 : 7} className="px-6 py-8 text-center text-[#94A3B8]">Aucune entrée enregistrée</td></tr>
                ) : entries.map(entry => (
                  <tr key={entry.entry_id} className="hover:bg-[#1E293B]/30 transition-colors">
                    <td className="px-6 py-4 text-[#94A3B8] text-sm">{fmtDate(entry.date)}</td>
                    <td className="px-6 py-4 text-[#F8FAFC] font-medium">{entry.name}</td>
                    <td className="px-6 py-4"><Badge className="bg-[#10B981]/20 text-[#10B981] border border-[#10B981]/30 text-xs capitalize">{getTypeLabel(entry.type)}</Badge></td>
                    <td className="px-6 py-4 text-[#10B981] font-bold">+{entry.quantity}</td>
                    <td className="px-6 py-4 text-[#94A3B8] capitalize">{entry.condition}</td>
                    <td className="px-6 py-4 text-[#94A3B8]">{entry.source || '-'}</td>
                    <td className="px-6 py-4"><Badge className="bg-[#2563EB]/20 text-[#2563EB] border border-[#2563EB]/30">{getZoneName(entry.zone_id)}</Badge></td>
                    {userRole === 'admin' && (
                      <td className="px-6 py-4 text-right">
                        <Button size="sm" variant="ghost" onClick={() => handleDeleteEntry(entry.entry_id)} className="text-[#EF4444] hover:text-white hover:bg-[#EF4444]"><Trash size={16} /></Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ======== TAB: DISTRIBUTIONS ======== */}
      {activeTab === TAB_DISTRIBUTIONS && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
              <Input value={distSearch} onChange={(e) => setDistSearch(e.target.value)} placeholder="Rechercher par agent..." className="bg-[#0F172A] border-[#1E293B] text-[#F8FAFC] pl-9" data-testid="search-distribution" />
            </div>
            <div className="sm:w-64">
              <SearchableSelect value={distAgentFilter} onValueChange={setDistAgentFilter} options={[{ value: 'all', label: 'Tous les agents' }, ...agents.filter(a => distributions.some(d => d.agent_id === a.agent_id)).map(a => ({ value: a.agent_id, label: a.name }))]} placeholder="Tous les agents" searchPlaceholder="Rechercher un agent..." testId="dist-agent-filter" />
            </div>
          </div>
          {(() => {
            const filteredDist = distributions.filter(d => (distAgentFilter === 'all' || d.agent_id === distAgentFilter) && (!distSearch || d.agent_name?.toLowerCase().includes(distSearch.toLowerCase())));
            return filteredDist.length === 0 ? (
            <div className="bg-[#0F172A] border border-[#1E293B] rounded-md p-8 text-center text-[#94A3B8]">Aucune distribution trouvée</div>
          ) : filteredDist.map(dist => (
            <div key={dist.distribution_id} className="bg-[#0F172A] border border-[#1E293B] rounded-md p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-[#2563EB] flex items-center justify-center text-white font-medium">{dist.agent_name?.charAt(0) || 'A'}</div>
                  <div>
                    <p className="text-[#F8FAFC] font-medium">{dist.agent_name}</p>
                    <p className="text-xs text-[#94A3B8]">{fmtDate(dist.date)} - {getZoneName(dist.zone_id)}</p>
                  </div>
                </div>
                <Badge className="bg-[#F59E0B]/20 text-[#F59E0B] border border-[#F59E0B]/30">{(dist.items || []).reduce((s, i) => s + i.quantity, 0)} article(s)</Badge>
                {userRole === 'admin' && (
                  <Button size="sm" variant="ghost" onClick={() => handleDeleteDist(dist.distribution_id)} className="text-[#EF4444] hover:text-white hover:bg-[#EF4444] ml-2"><Trash size={16} /></Button>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {(dist.items || []).map((item, idx) => (
                  <Badge key={`${item.name}-${idx}`} className="bg-[#1E293B] text-[#F8FAFC] border border-[#334155] px-3 py-1">
                    {item.name} {item.type ? `(${getTypeLabel(item.type)})` : ''} x{item.quantity}
                  </Badge>
                ))}
              </div>
            </div>
          ));
          })()}
        </div>
      )}

      {/* Fiches Matériel par Agent */}
      {activeTab === TAB_DISTRIBUTIONS && (
      <div className="bg-[#0F172A] border border-[#1E293B] rounded-md p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <h2 className="text-xl font-bold text-[#F8FAFC]">Fiches Matériel par Agent</h2>
          <div className="relative sm:w-64">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
            <Input value={ficheSearch} onChange={(e) => setFicheSearch(e.target.value)} placeholder="Rechercher un agent..." className="bg-[#1E293B] border-[#334155] text-[#F8FAFC] pl-9 h-9" data-testid="search-fiche-agent" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {agents.filter(a => distributions.some(d => d.agent_id === a.agent_id) && (!ficheSearch || a.name?.toLowerCase().includes(ficheSearch.toLowerCase()))).map(agent => (
            <div key={agent.agent_id} className="flex items-center justify-between p-4 bg-[#1E293B]/30 rounded-md border border-[#334155]/50 hover:border-[#2563EB]/50 transition-colors">
              <div className="flex items-center gap-3">
                {agent.photo_url ? <img src={agent.photo_url} alt="" className="h-9 w-9 rounded-full object-cover" /> :
                  <div className="h-9 w-9 rounded-full bg-[#2563EB] flex items-center justify-center text-white text-sm font-medium">{agent.name?.charAt(0)}</div>}
                <p className="text-sm font-medium text-[#F8FAFC]">{agent.name}</p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/logistics/agent-report/${agent.agent_id}/pdf`, '_blank')} className="text-[#2563EB] hover:text-white hover:bg-[#2563EB]" title="Rapport matériel PDF" data-testid={`agent-material-pdf-${agent.agent_id}`}>
                <FilePdf size={18} />
              </Button>
            </div>
          ))}
          {agents.filter(a => distributions.some(d => d.agent_id === a.agent_id)).length === 0 && (
            <p className="text-[#94A3B8] text-sm col-span-3">Aucune distribution effectuée pour le moment.</p>
          )}
        </div>
      </div>
      )}

      {/* ======== TAB: BESOINS D'ACHAT ======== */}
      {activeTab === TAB_NEEDS && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
              <Input value={needSearch} onChange={(e) => setNeedSearch(e.target.value)} placeholder="Rechercher un besoin..." className="bg-[#0F172A] border-[#1E293B] text-[#F8FAFC] pl-9" data-testid="search-need" />
            </div>
            <Button onClick={() => setShowNeedDialog(true)} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="add-need-button">
              <Plus size={18} className="mr-2" /> Nouveau besoin
            </Button>
          </div>
          {(() => {
            const filteredNeeds = needs.filter(n => !needSearch || n.description?.toLowerCase().includes(needSearch.toLowerCase()));
            return filteredNeeds.length === 0 ? (
              <div className="bg-[#0F172A] border border-[#1E293B] rounded-md p-8 text-center text-[#94A3B8]">Aucun besoin d'achat enregistré</div>
            ) : (
              <div className="space-y-3">
                {filteredNeeds.map(need => (
                  <div key={need.need_id} className={`bg-[#0F172A] border rounded-md p-4 ${need.urgent && need.status === 'pending' ? 'border-[#EF4444]/50' : 'border-[#1E293B]'}`} data-testid={`need-${need.need_id}`}>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className={`font-medium ${need.status === 'done' ? 'text-[#64748B] line-through' : 'text-[#F8FAFC]'}`}>{need.description}</p>
                          {need.urgent && need.status === 'pending' && <Badge className="bg-[#EF4444]/20 text-[#EF4444] border border-[#EF4444]/30 text-xs">URGENT</Badge>}
                          {need.status === 'done' && <Badge className="bg-[#10B981]/20 text-[#10B981] border border-[#10B981]/30 text-xs">Acheté</Badge>}
                        </div>
                        <p className="text-xs text-[#94A3B8] mt-1">
                          {need.quantity} × {need.unit_price?.toFixed(2)} $ = <span className="text-[#F8FAFC] font-semibold">{need.total?.toFixed(2)} $</span>
                          {need.due_date && ` • À faire avant le ${new Date(need.due_date).toLocaleDateString('fr-FR')}`}
                          {need.created_by_name && ` • Par ${need.created_by_name}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 self-end sm:self-auto flex-shrink-0">
                        {need.status === 'pending' && (
                          <Button size="sm" onClick={() => handleNeedStatus(need.need_id, 'done')} className="bg-[#10B981] hover:bg-[#059669] text-white h-8 text-xs" data-testid={`need-done-${need.need_id}`}>Acheté</Button>
                        )}
                        {userRole === 'admin' && (
                          <Button size="sm" variant="ghost" onClick={() => handleNeedDelete(need.need_id)} className="text-[#EF4444] hover:text-white hover:bg-[#EF4444] h-8 w-8 p-0"><Trash size={15} /></Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {/* ======== DIALOG: NOUVEAU BESOIN ======== */}
      <Dialog open={showNeedDialog} onOpenChange={setShowNeedDialog}>
        <DialogContent className="bg-[#0F172A] border-[#1E293B] text-[#F8FAFC] max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-xl font-bold">Nouveau besoin d'achat</DialogTitle></DialogHeader>
          <form onSubmit={handleNeedSubmit} className="space-y-4">
            <div>
              <Label className="text-[#F8FAFC]">Description du besoin *</Label>
              <Input value={needForm.description} onChange={e => setNeedForm({ ...needForm, description: e.target.value })} required placeholder="Ex: 20 paires de bottines taille 42" className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" data-testid="need-description" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-[#F8FAFC]">Article (pour le stock) *</Label>
                <Input value={needForm.item_name} onChange={e => setNeedForm({ ...needForm, item_name: e.target.value })} required placeholder="Ex: Bottines T42" className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" data-testid="need-item-name" />
                <p className="text-[10px] text-[#64748B] mt-1">Sera ajouté automatiquement au stock une fois l'achat confirmé</p>
              </div>
              <div>
                <Label className="text-[#F8FAFC]">Type de matériel *</Label>
                <div className="mt-1">
                  <SearchableSelect value={needForm.item_type} onValueChange={v => setNeedForm({ ...needForm, item_type: v })} options={STOCK_TYPES} placeholder="Choisir le type" searchPlaceholder="Rechercher un type..." testId="need-item-type-select" />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label className="text-[#F8FAFC]">Quantité</Label>
                <Input type="number" min="1" value={needForm.quantity} onChange={e => setNeedForm({ ...needForm, quantity: parseInt(e.target.value) || 1 })} required className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" data-testid="need-quantity" />
              </div>
              <div>
                <Label className="text-[#F8FAFC]">Prix unitaire ($)</Label>
                <Input type="number" min="0" step="0.01" value={needForm.unit_price} onChange={e => setNeedForm({ ...needForm, unit_price: e.target.value })} required placeholder="0.00" className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" data-testid="need-unit-price" />
              </div>
              <div>
                <Label className="text-[#F8FAFC]">Montant total</Label>
                <div className="h-10 flex items-center px-3 bg-[#1E293B]/50 border border-[#334155] rounded-md text-[#10B981] font-semibold text-sm">
                  {((needForm.quantity || 0) * (parseFloat(needForm.unit_price) || 0)).toFixed(2)} $
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
              <div>
                <Label className="text-[#F8FAFC]">Date limite (optionnel)</Label>
                <Input type="date" value={needForm.due_date} onChange={e => setNeedForm({ ...needForm, due_date: e.target.value })} className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" data-testid="need-due-date" />
              </div>
              <button type="button" onClick={() => setNeedForm({ ...needForm, urgent: !needForm.urgent })} className={`h-10 rounded-md border text-sm font-medium transition-colors ${needForm.urgent ? 'bg-[#EF4444] border-[#EF4444] text-white' : 'bg-[#1E293B] border-[#334155] text-[#94A3B8] hover:border-[#EF4444]'}`} data-testid="need-urgent-toggle">
                {needForm.urgent ? '🔴 URGENT' : 'Marquer comme urgent'}
              </button>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowNeedDialog(false)} className="border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B]">Annuler</Button>
              <Button type="submit" className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="save-need-button">Enregistrer</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ======== DIALOG: ENTRÉE STOCK ======== */}
      <Dialog open={showEntryDialog} onOpenChange={setShowEntryDialog}>
        <DialogContent className="bg-[#0F172A] border-[#1E293B] text-[#F8FAFC] max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-xl font-bold">Nouvelle Entrée Stock</DialogTitle></DialogHeader>
          <form onSubmit={handleEntrySubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-[#F8FAFC]">Nom de l'article</Label>
                <Input value={entryForm.name} onChange={e => setEntryForm({ ...entryForm, name: e.target.value })} required placeholder="Bottine taille 42..." className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" data-testid="entry-name" />
              </div>
              <div>
                <Label className="text-[#F8FAFC]">Zone</Label>
                <Select value={entryForm.zone_id} onValueChange={v => setEntryForm({ ...entryForm, zone_id: v })}>
                  <SelectTrigger className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0F172A] border-[#1E293B]">
                    {zones.map(z => <SelectItem key={z.zone_id} value={z.zone_id} className="text-[#F8FAFC]">{z.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-[#F8FAFC]">Catégorie</Label>
                <Select value={entryForm.category} onValueChange={v => setEntryForm({ ...entryForm, category: v })}>
                  <SelectTrigger className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0F172A] border-[#1E293B]">
                    <SelectItem value="uniform" className="text-[#F8FAFC]">Uniforme</SelectItem>
                    <SelectItem value="equipment" className="text-[#F8FAFC]">Équipement</SelectItem>
                    <SelectItem value="tool" className="text-[#F8FAFC]">Outil</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#F8FAFC]">Type</Label>
                <div className="flex flex-wrap gap-1.5 mt-1 p-2 bg-[#1E293B] border border-[#334155] rounded-md min-h-[40px]">
                  {[...STOCK_TYPES, ...((entryForm.type || '').split(',').filter(Boolean).filter(t => !STOCK_TYPES.some(st => st.value === t)).map(t => ({ value: t, label: t })))].map(type => {
                    const selected = (entryForm.type || '').split(',').filter(Boolean).includes(type.value);
                    return (
                      <button key={type.value} type="button" onClick={() => {
                        const current = (entryForm.type || '').split(',').filter(Boolean);
                        const updated = selected ? current.filter(t => t !== type.value) : [...current, type.value];
                        setEntryForm({ ...entryForm, type: updated.join(',') });
                      }} className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${selected ? 'bg-[#2563EB] text-white border-[#2563EB]' : 'bg-[#0F172A] text-[#94A3B8] border-[#334155] hover:border-[#2563EB]'}`}>
                        {type.label}
                      </button>
                    );
                  })}
                </div>
                <div className="flex gap-2 mt-2">
                  <Input value={customType} onChange={e => setCustomType(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); const v = customType.trim().toLowerCase(); if (v) { const current = (entryForm.type || '').split(',').filter(Boolean); if (!current.includes(v)) setEntryForm({ ...entryForm, type: [...current, v].join(',') }); setCustomType(''); } } }} placeholder="Autre type de matériel..." className="bg-[#1E293B] border-[#334155] text-[#F8FAFC] h-9 text-sm" data-testid="custom-type-input" />
                  <Button type="button" onClick={() => { const v = customType.trim().toLowerCase(); if (v) { const current = (entryForm.type || '').split(',').filter(Boolean); if (!current.includes(v)) setEntryForm({ ...entryForm, type: [...current, v].join(',') }); setCustomType(''); } }} className="bg-[#10B981] hover:bg-[#059669] text-white px-3 h-9" data-testid="add-custom-type-button"><Plus size={15} /></Button>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label className="text-[#F8FAFC]">Quantité</Label>
                <Input type="number" min="1" value={entryForm.quantity} onChange={e => setEntryForm({ ...entryForm, quantity: parseInt(e.target.value) || 1 })} required className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" data-testid="entry-quantity" />
              </div>
              <div>
                <Label className="text-[#F8FAFC]">État</Label>
                <Select value={entryForm.condition} onValueChange={v => setEntryForm({ ...entryForm, condition: v })}>
                  <SelectTrigger className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0F172A] border-[#1E293B]">
                    <SelectItem value="new" className="text-[#F8FAFC]">Neuf</SelectItem>
                    <SelectItem value="good" className="text-[#F8FAFC]">Bon</SelectItem>
                    <SelectItem value="fair" className="text-[#F8FAFC]">Moyen</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#F8FAFC]">Date</Label>
                <Input type="date" value={entryForm.date} onChange={e => setEntryForm({ ...entryForm, date: e.target.value })} className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" />
              </div>
            </div>
            <div>
              <Label className="text-[#F8FAFC]">Source / Fournisseur</Label>
              <Input value={entryForm.source} onChange={e => setEntryForm({ ...entryForm, source: e.target.value })} placeholder="Achat, Donation, Transfert..." className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowEntryDialog(false)} className="border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B]">Annuler</Button>
              <Button type="submit" className="bg-[#10B981] hover:bg-[#059669] text-white" data-testid="save-entry-button">Enregistrer l'entrée</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ======== DIALOG: DISTRIBUTION ======== */}
      <Dialog open={showDistDialog} onOpenChange={(open) => { setShowDistDialog(open); if (!open) setDistItems([]); }}>
        <DialogContent className="bg-[#0F172A] border-[#1E293B] text-[#F8FAFC] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-xl font-bold">Distribution à un Agent</DialogTitle></DialogHeader>
          <form onSubmit={handleDistSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-[#F8FAFC]">Agent</Label>
                <Select value={distForm.agent_id} onValueChange={v => setDistForm({ ...distForm, agent_id: v })}>
                  <SelectTrigger className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" data-testid="dist-agent-select"><SelectValue placeholder="Sélectionner un agent" /></SelectTrigger>
                  <SelectContent className="bg-[#0F172A] border-[#1E293B]">
                    {agents.map(a => <SelectItem key={a.agent_id} value={a.agent_id} className="text-[#F8FAFC]">{a.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#F8FAFC]">Date</Label>
                <Input type="date" value={distForm.date} onChange={e => setDistForm({ ...distForm, date: e.target.value })} className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" />
              </div>
            </div>

            {/* Articles disponibles */}
            <div>
              <Label className="text-[#F8FAFC]">Ajouter des articles</Label>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[200px] overflow-y-auto p-2 bg-[#1E293B]/30 rounded-md border border-[#334155]">
                {items.filter(i => (i.quantity_available || 0) > 0).map(item => (
                  <button key={item.item_id} type="button" onClick={() => addDistItem(item.item_id)}
                    className={`flex items-center justify-between p-2.5 rounded text-left text-sm border transition-colors ${
                      distItems.find(d => d.item_id === item.item_id) ? 'bg-[#2563EB]/20 border-[#2563EB]/50 text-[#F8FAFC]' : 'bg-[#0F172A] border-[#334155] text-[#94A3B8] hover:border-[#2563EB]'
                    }`} data-testid={`add-dist-item-${item.item_id}`}>
                    <span>{item.name}</span>
                    <span className="text-xs text-[#10B981]">{item.quantity_available} dispo</span>
                  </button>
                ))}
                {items.filter(i => (i.quantity_available || 0) > 0).length === 0 && (
                  <p className="text-[#94A3B8] text-sm col-span-2 p-2">Aucun article disponible en stock</p>
                )}
              </div>
            </div>

            {/* Articles sélectionnés */}
            {distItems.length > 0 && (
              <div>
                <Label className="text-[#F8FAFC]">Articles à distribuer</Label>
                <div className="mt-2 space-y-2">
                  {distItems.map(item => (
                    <div key={item.item_id} className="flex items-center gap-3 bg-[#1E293B] p-3 rounded-md border border-[#334155]">
                      <span className="text-[#F8FAFC] flex-1">{item.name}</span>
                      <Input type="number" min="1" max={item.max} value={item.quantity}
                        onChange={e => updateDistItemQty(item.item_id, parseInt(e.target.value) || 1)}
                        className="w-20 bg-[#0F172A] border-[#334155] text-[#F8FAFC] text-center" />
                      <span className="text-xs text-[#94A3B8]">/ {item.max}</span>
                      <button type="button" onClick={() => removeDistItem(item.item_id)} className="text-[#EF4444] hover:text-white text-sm">✕</button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => { setShowDistDialog(false); setDistItems([]); }} className="border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B]">Annuler</Button>
              <Button type="submit" className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="save-distribution-button">
                Distribuer ({distItems.reduce((s, i) => s + i.quantity, 0)} article(s))
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ======== DIALOG: MODIFIER ARTICLE STOCK ======== */}
      <Dialog open={showEditStockDialog} onOpenChange={(open) => { setShowEditStockDialog(open); if (!open) setEditingStock(null); }}>
        <DialogContent className="bg-[#0F172A] border-[#1E293B] text-[#F8FAFC] max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-xl font-bold">Modifier l'article</DialogTitle></DialogHeader>
          {editingStock && (
            <form onSubmit={handleEditStockSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#F8FAFC]">Nom</Label>
                  <Input value={editingStock.name} onChange={e => setEditingStock({ ...editingStock, name: e.target.value })} required className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" />
                </div>
                <div>
                  <Label className="text-[#F8FAFC]">Catégorie</Label>
                  <Select value={editingStock.category} onValueChange={v => setEditingStock({ ...editingStock, category: v })}>
                    <SelectTrigger className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0F172A] border-[#1E293B]">
                      <SelectItem value="uniform" className="text-[#F8FAFC]">Uniforme</SelectItem>
                      <SelectItem value="equipment" className="text-[#F8FAFC]">Équipement</SelectItem>
                      <SelectItem value="tool" className="text-[#F8FAFC]">Outil</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label className="text-[#F8FAFC]">Stock total</Label>
                  <Input type="number" min="0" value={editingStock.quantity} onChange={e => setEditingStock({ ...editingStock, quantity: parseInt(e.target.value) || 0 })} className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" />
                </div>
                <div>
                  <Label className="text-[#F8FAFC]">Disponible</Label>
                  <Input type="number" min="0" value={editingStock.quantity_available} onChange={e => setEditingStock({ ...editingStock, quantity_available: parseInt(e.target.value) || 0 })} className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" />
                </div>
                <div>
                  <Label className="text-[#F8FAFC]">État</Label>
                  <Select value={editingStock.condition} onValueChange={v => setEditingStock({ ...editingStock, condition: v })}>
                    <SelectTrigger className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0F172A] border-[#1E293B]">
                      <SelectItem value="new" className="text-[#F8FAFC]">Neuf</SelectItem>
                      <SelectItem value="good" className="text-[#F8FAFC]">Bon</SelectItem>
                      <SelectItem value="fair" className="text-[#F8FAFC]">Moyen</SelectItem>
                      <SelectItem value="poor" className="text-[#F8FAFC]">Mauvais</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowEditStockDialog(false)} className="border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B]">Annuler</Button>
                <Button type="submit" className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white">Enregistrer</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Logistics;
